'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'

/** The comp's eight tiles, in its order, with its words and its grid. */
const CARDS = [
  {
    n: '01',
    title: 'Strategia',
    text: 'Publikujesz, ale nie wiesz, co właściwie działa? Ustalamy, do kogo mówisz, w jakich formatach oraz jak często publikujesz.',
    path: 'strategia',
    look: 'w',
    col: '5 / 8',
  },
  {
    n: '02',
    title: 'Studio',
    text: 'Masz świetny pomysł na nagranie i brakuje Ci miejsca do jego realizacji? W naszych studiach technik obsłuży Twoje nagranie od A do Z.',
    path: 'studio',
    look: 'p',
    col: '8 / 13',
  },
  {
    n: '03',
    title: 'Sprzęt',
    text: 'Aparaty, obiektywy, światło i dźwięk wypożyczysz we FLOU na chwilę. Sprzęt odbierzesz w wygodnym dla Ciebie miejscu. Do jednej produkcji wystarczy wynajem zamiast zakupu nowego aparatu.',
    path: 'wynajem',
    look: 'd',
    col: '5 / 10',
  },
  {
    n: '04',
    title: 'Operator',
    text: 'Nie stresuj się nagraniem swojego filmu. Zarezerwuj operatora kilkoma kliknięciami i ciesz się najlepszą jakością nagrań.',
    path: 'operator',
    look: 'p',
    col: '1 / 6',
  },
  {
    n: '05',
    title: 'Fotograf',
    text: 'Zdjęcia z telefonu nie oddają klasy Twojej marki? Nasz fotograf zadba o światło i kadr Twoich zdjęć.',
    path: 'fotograf',
    look: 'd',
    col: '10 / 13',
  },
  {
    n: '06',
    title: 'Montaż',
    text: 'Montaż zjada najwięcej godzin przy tworzeniu contentu. Oddaj nam surowy materiał i odbierz gotowy odcinek z cięciami, tempem, napisami oraz dźwiękiem dopasowanymi pod Ciebie.',
    path: 'montaz',
    look: 'w',
    col: '1 / 5',
  },
  {
    n: '07',
    title: 'Grafika',
    text: 'Nawet świetny film przegrywa przez słabą miniaturę. Oddaj nam projekt miniatur, okładek, plakatów oraz prezentacji i zbieraj więcej kliknięć.',
    path: 'grafika',
    look: 'd',
    col: '5 / 9',
  },
  {
    n: '08',
    title: 'Strony internetowe',
    text: 'Potrzebujesz swojego miejsca w sieci, ale kodowanie Cię przerasta? Strony, sklepy oraz aplikacje zbudują dla Ciebie nasi doświadczeni programiści.',
    path: 'kontakt',
    look: 'p',
    col: '9 / 13',
  },
] as const

/** The word wheel, and which card (by number) each word belongs to. */
const WORDS = [
  ['Miniaturki', '07'],
  ['Montaż', '06'],
  ['Grafika', '07'],
  ['Studio', '02'],
  ['Fotograf', '05'],
  ['Operator', '04'],
] as const

const LOOK = {
  w: { card: 'fh-svc fh-svc-w fh-pearl', num: 'fh-st fh-num fh-gs' },
  p: { card: 'fh-svc fh-svc-p fh-gg', num: 'fh-st fh-num' },
  d: { card: 'fh-svc fh-svc-d fh-gg', num: 'fh-st fh-num fh-gt' },
} as const

const BLUR = [0, 2.2, 4.5]
const OPACITY = [1, 0.5, 0.25]
const SIZE = [3.3, 2.6, 2.2]
const PAD = [32, 16, 0]

function Card({
  index,
  locale,
  onPick,
  onLeave,
}: {
  index: number
  locale: string
  onPick: () => void
  onLeave: () => void
}) {
  const card = CARDS[index]
  if (!card) return null
  const look = LOOK[card.look]
  return (
    <Link
      href={`/${locale}/${card.path}`}
      className={look.card}
      style={{ gridColumn: card.col }}
      onMouseEnter={onPick}
      onMouseLeave={onLeave}
    >
      <span className={look.num}>{card.n}</span>
      <h3>{card.title}</h3>
      <p>{card.text}</p>
    </Link>
  )
}

/**
 * Usługi: the drifting word wheel, eight tiles, and the FLOU mark over water.
 *
 * The wheel advances every 2.4s; hovering a tile picks its word and holds the
 * wheel until the pointer leaves. Hovering any tile blurs and dims the rest
 * (pure CSS, `.fh-svc-grid:has(...)`).
 */
export function HomeServices({ locale }: { locale: string }) {
  const [word, setWord] = useState(2)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused) return
    const timer = setInterval(() => setWord((was) => (was + 1) % WORDS.length), 2400)
    return () => clearInterval(timer)
  }, [paused])

  const pickCard = (n: string) => () => {
    const found = WORDS.findIndex(([, card]) => card === n)
    if (found >= 0) setWord(found)
    setPaused(true)
  }
  const resume = () => setPaused(false)
  const card = (i: number) => {
    const n = CARDS[i]?.n ?? ''
    return <Card key={n} index={i} locale={locale} onPick={pickCard(n)} onLeave={resume} />
  }

  return (
    <section id="uslugi" aria-labelledby="svc-d" className="fh-wrap" style={{ marginTop: 120 }}>
      <h2 id="svc-d" className="sr-only">
        Usługi FLOU
      </h2>
      <div
        className="fh-g12 fh-svc-grid"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 16 }}
      >
        <div
          className="fh-hide-m"
          aria-hidden="true"
          style={{
            gridColumn: '1 / 5',
            gridRow: '1 / 3',
            position: 'relative',
            minHeight: 440,
            overflow: 'clip',
            overflowClipMargin: 40,
          }}
          onMouseLeave={resume}
        >
          <div
            className="fh-float"
            style={{
              position: 'absolute',
              inset: 0,
              transform: 'rotate(-8deg)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            {WORDS.map(([name], k) => {
              const d = Math.min(Math.abs(k - word), 2)
              const pick = () => {
                setWord(k)
                setPaused(true)
              }
              return (
                <button
                  key={name}
                  type="button"
                  tabIndex={-1}
                  className={d === 0 ? 'fh-sw fh-on' : 'fh-sw'}
                  style={{
                    filter: `blur(${BLUR[d]}px)`,
                    opacity: OPACITY[d],
                    fontSize: `${SIZE[d]}rem`,
                    paddingLeft: PAD[d],
                  }}
                  onMouseEnter={pick}
                  onFocus={pick}
                  onClick={pick}
                >
                  {name}
                </button>
              )
            })}
          </div>
        </div>
        {card(0)}
        {card(1)}
        {card(2)}
        {card(3)}
        <div className="fh-lh-wrap" style={{ gridColumn: '6 / 10' }} role="img" aria-label="FLOU">
          <span className="fh-lh-bloom" aria-hidden="true" />
          <span className="fh-lh-tilt" aria-hidden="true">
            <span className="fh-lh-glass" />
            <span className="fh-lh-edge" />
          </span>
          <span className="fh-lh-reflect" aria-hidden="true" />
          <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
            <filter id="flou-water">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.012 0.09"
                numOctaves={2}
                seed={4}
              />
              <feDisplacementMap
                in="SourceGraphic"
                scale={9}
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          </svg>
        </div>
        {card(4)}
        {card(5)}
        {card(6)}
        {card(7)}
      </div>
    </section>
  )
}
